import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Avatar,
  Badge,
  Button,
  Checkbox,
  useToast,
  Spinner,
  Center,
  Tooltip,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, UserPlus, Check, Users, AlertCircle } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useSearchUsers, useInviteMembers } from '@/api';
import { CollaborativeMember } from '@/types/collaborative/collaborativeList';
import { UserSearchResult } from '@/types/social';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

const MotionBox = motion.create(Box);

interface InviteMembersTabProps {
  urlKey: string;
  existingMembers: CollaborativeMember[];
  maxMembers: number;
  onInviteComplete?: () => void;
}

// Helper to get display name from UserSearchResult
const getDisplayName = (user: UserSearchResult): string => {
  if (user.name) return user.name;
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.firstName) return user.firstName;
  return `Member ${user.id}`;
};

/**
 * UserSelectionCard - Individual user card for selection
 */
const UserSelectionCard: React.FC<{
  user: UserSearchResult;
  isSelected: boolean;
  isAlreadyMember: boolean;
  isPending: boolean;
  isIneligible: boolean;
  onToggle: () => void;
}> = ({ user, isSelected, isAlreadyMember, isPending, isIneligible, onToggle }) => {
  const displayName = getDisplayName(user);
  const isDisabled = isAlreadyMember || isIneligible;

  return (
    <MotionBox
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      <HStack
        p={3}
        bg={isSelected ? 'purple.50' : isAlreadyMember ? 'var(--card-surface-2)' : isIneligible ? 'orange.50' : 'var(--card-bg)'}
        borderRadius="var(--r-inner)"
        border="2px solid"
        borderColor={isSelected ? 'purple.400' : isAlreadyMember ? 'var(--card-divider)' : isIneligible ? 'orange.200' : 'var(--card-divider)'}
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        onClick={onToggle}
        opacity={isAlreadyMember ? 0.6 : 1}
        transition="all 0.2s"
        _hover={isDisabled ? {} : { borderColor: 'purple.300', bg: 'purple.50' }}
      >
        <Avatar
          size="sm"
          name={displayName}
          src={user.photoUrl || undefined}
          border="2px solid"
          borderColor={isSelected ? 'purple.400' : 'var(--card-divider)'}
        />
        <VStack align="start" spacing={0} flex={1}>
          <HStack spacing={1}>
            <Text fontWeight="medium" fontSize="sm" color="var(--card-text)">
              {displayName}
            </Text>
          </HStack>
          {user.location && (
            <Text fontSize="xs" color="var(--card-text-3)">
              {user.location}
            </Text>
          )}
        </VStack>

        {/* Status indicators */}
        {isAlreadyMember && (
          <Badge bg="gray.500" color="white" borderRadius="full" fontSize="xs">
            Member
          </Badge>
        )}
        {isPending && (
          <Badge bg="orange.500" color="white" borderRadius="full" fontSize="xs">
            Pending
          </Badge>
        )}
        {isIneligible && !isAlreadyMember && (
          <Tooltip label="Not friends with all members" placement="left">
            <Box color="orange.500">
              <AlertCircle size={18} />
            </Box>
          </Tooltip>
        )}
        {isSelected && !isDisabled && (
          <Box bg="purple.500" borderRadius="full" p={1}>
            <Check size={14} color="white" />
          </Box>
        )}
        {!isSelected && !isDisabled && !isPending && (
          <Checkbox
            isChecked={isSelected}
            sx={{
              '.chakra-checkbox__control[data-checked]': {
                bg: 'purple.500',
                borderColor: 'purple.500',
              },
            }}
            pointerEvents="none"
          />
        )}
      </HStack>
    </MotionBox>
  );
};

/**
 * InviteMembersTab - Tab content for inviting users to a collaborative list
 */
export function InviteMembersTab({
  urlKey,
  existingMembers,
  maxMembers,
  onInviteComplete,
}: InviteMembersTabProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [selectedUsers, setSelectedUsers] = useState<UserSearchResult[]>([]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // IDs of accepted and pending non-creator members — used to check mutual friendships
  const memberIdsToCheck = useMemo(() =>
    existingMembers
      .filter(m => (m.status === 'accepted' || m.status === 'pending') && !m.isCreator)
      .map(m => m.user.id),
    [existingMembers]
  );

  // Name lookup for existing members (for warning messages)
  const memberNameById = useMemo(() => {
    const map: Record<number, string> = {};
    existingMembers.forEach(m => { map[m.user.id] = m.user.name; });
    return map;
  }, [existingMembers]);

  // Search friends only; pass existing member IDs for mutual-friendship check
  const { data: searchResults, isLoading: isSearching } = useSearchUsers(
    { q: debouncedQuery, limit: 20, friendsOnly: true, memberIds: memberIdsToCheck },
    { enabled: debouncedQuery.length >= 2 }
  );
  const { mutateAsync: inviteMembers, isPending: isInviting } = useInviteMembers();

  // Create sets for existing/pending members
  const existingMemberIds = useMemo(() =>
    new Set(existingMembers.filter(m => m.status === 'accepted').map(m => m.user.id)),
    [existingMembers]
  );
  const pendingMemberIds = useMemo(() =>
    new Set(existingMembers.filter(m => m.status === 'pending').map(m => m.user.id)),
    [existingMembers]
  );

  const filteredUsers = useMemo(() => searchResults || [], [searchResults]);

  // Calculate remaining slots
  const currentMemberCount = existingMembers.length;
  const remainingSlots = maxMembers - currentMemberCount;
  const canInviteMore = selectedIds.size < remainingSlots;

  const handleToggleSelection = async (user: UserSearchResult) => {
    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch {
      // Haptics not available
    }

    const userId = user.id;

    // Block ineligible users with a specific warning
    if (user.canInvite === false && user.notFriendsWith?.length) {
      const blockingNames = user.notFriendsWith
        .map(id => memberNameById[id])
        .filter(Boolean)
        .join(', ');
      toast({
        title: 'Cannot invite',
        description: `${getDisplayName(user)} and ${blockingNames} must be friends first before joining this group.`,
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    if (selectedIds.has(userId)) {
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
      setSelectedUsers(prev => prev.filter(u => u.id !== userId));
    } else if (canInviteMore) {
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        newSet.add(userId);
        return newSet;
      });
      setSelectedUsers(prev => [...prev, user]);
    } else {
      toast({
        title: 'Maximum members reached',
        description: `You can only invite ${remainingSlots} more member${remainingSlots !== 1 ? 's' : ''}.`,
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleInvite = async () => {
    if (selectedIds.size === 0) return;

    try {
      await Haptics.impact({ style: ImpactStyle.Medium });
    } catch {
      // Haptics not available
    }

    try {
      const result = await inviteMembers({
        urlKey,
        userIds: Array.from(selectedIds),
      });

      const successCount = result.invited?.length || 0;
      const errorCount = result.errors?.length || 0;

      if (successCount > 0) {
        toast({
          title: 'Invitations sent!',
          description: `${successCount} friend${successCount !== 1 ? 's' : ''} invited.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      if (errorCount > 0) {
        toast({
          title: 'Some invitations failed',
          description: `${errorCount} invitation${errorCount !== 1 ? 's' : ''} could not be sent.`,
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
      }

      setSelectedIds(new Set());
      setSelectedUsers([]);
      onInviteComplete?.();
    } catch (error) {
      toast({
        title: 'Failed to send invitations',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* Slots indicator */}
      <HStack justify="space-between" px={1}>
        <HStack spacing={2}>
          <Users size={16} color="var(--chakra-colors-gray-500)" />
          <Text fontSize="sm" color="var(--card-text-2)">
            {currentMemberCount}/{maxMembers} members
          </Text>
        </HStack>
        {selectedIds.size > 0 && (
          <Badge bg="purple.500" color="white" borderRadius="full">
            {selectedIds.size} selected
          </Badge>
        )}
      </HStack>

      {/* Search input */}
      <InputGroup>
        <InputLeftElement pointerEvents="none">
          <Search size={18} color="var(--chakra-colors-gray-400)" />
        </InputLeftElement>
        <Input
          placeholder="Search your friends..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          borderRadius="full"
          bg="var(--card-surface-2)"
          _focus={{ bg: 'var(--card-bg)', borderColor: 'purple.400' }}
          data-testid="collab-invite-search"
        />
      </InputGroup>

      {/* Friend hint */}
      <Text fontSize="xs" color="var(--card-text-3)" textAlign="center">
        Don't see someone? Make sure they are your{' '}
        <Text
          as="span"
          color="purple.500"
          textDecoration="underline"
          cursor="pointer"
          onClick={() => navigate({ to: '/friends' })}
        >
          friend
        </Text>
      </Text>

      {/* Selected users (always visible) */}
      {selectedUsers.length > 0 && (
        <Box>
          <Text fontSize="xs" color="var(--card-text-3)" mb={2} fontWeight="medium">
            Selected ({selectedUsers.length})
          </Text>
          <VStack spacing={2} align="stretch">
            <AnimatePresence>
              {selectedUsers.map(user => (
                <UserSelectionCard
                  key={`selected-${user.id}`}
                  user={user}
                  isSelected={true}
                  isAlreadyMember={existingMemberIds.has(user.id)}
                  isPending={pendingMemberIds.has(user.id)}
                  isIneligible={false}
                  onToggle={() => handleToggleSelection(user)}
                />
              ))}
            </AnimatePresence>
          </VStack>
        </Box>
      )}

      {/* Search results */}
      <Box maxH="250px" overflowY="auto">
        {searchQuery.length < 2 ? (
          <Center py={8}>
            <VStack spacing={2}>
              <Search size={32} color="var(--chakra-colors-gray-400)" />
              <Text color="var(--card-text-3)" textAlign="center" fontSize="sm">
                Type at least 2 characters to search
              </Text>
            </VStack>
          </Center>
        ) : isSearching ? (
          <Center py={8}>
            <Spinner color="purple.500" />
          </Center>
        ) : filteredUsers.length === 0 ? (
          <Center py={8}>
            <VStack spacing={2}>
              <Users size={32} color="var(--chakra-colors-gray-400)" />
              <Text color="var(--card-text-3)" textAlign="center">
                No friends found for "{searchQuery}"
              </Text>
            </VStack>
          </Center>
        ) : (
          <VStack spacing={2} align="stretch">
            <Text fontSize="xs" color="var(--card-text-3)" mb={1} fontWeight="medium">
              Friends
            </Text>
            <AnimatePresence>
              {filteredUsers
                .filter(user => !selectedIds.has(user.id))
                .map(user => (
                  <UserSelectionCard
                    key={user.id}
                    user={user}
                    isSelected={selectedIds.has(user.id)}
                    isAlreadyMember={existingMemberIds.has(user.id)}
                    isPending={pendingMemberIds.has(user.id)}
                    isIneligible={user.canInvite === false}
                    onToggle={() => handleToggleSelection(user)}
                  />
                ))}
            </AnimatePresence>
          </VStack>
        )}
      </Box>

      {/* Invite button */}
      <Button
        bg="purple.500"
        color="white"
        _hover={{ bg: 'purple.600' }}
        onClick={handleInvite}
        isLoading={isInviting}
        isDisabled={selectedIds.size === 0}
        leftIcon={<UserPlus size={16} />}
        w="100%"
        size="lg"
        data-testid="collab-invite-submit"
      >
        Invite {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
      </Button>
    </VStack>
  );
}
